import { PriceListDetail } from "@legacy-megarax/crm-contracts";
import { BreadcrumbMarkers } from "@legacy-megarax/ui-components";
import { Container, Tab, Tabs } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom-v5-compat";

import { getPriceListDetailsUrl, getPriceListHomeUrl } from "../routes";
import { PriceListCurrentContainer } from "./Current";
import { TradeItemDictionary } from "./PriceListDetailsContainer";
import { PriceListName } from "./PriceListName";
import { ProposalsContainer } from "./Proposals";

export type PriceListTab = "current" | "proposals";

interface Props {
  priceListDetail: PriceListDetail;
  tradeItems: TradeItemDictionary | null;
  switchTab: (uuid: string) => (tab: PriceListTab) => void;
  permissions: {
    viewProposals: boolean;
  };
  reload: () => void;
  renamePriceList: (name: string) => Promise<void>;
}

export const PriceListDetailsView: React.FC<Props> = ({
  priceListDetail,
  switchTab,
  permissions,
  tradeItems,
  renamePriceList,
  reload,
}) => {
  const classes = useStyles();
  const params = useParams<{ uuid: string; tab: PriceListTab }>();
  const navigate = useNavigate();

  const uuid = params.uuid || "";

  const handleChange = (e: React.ChangeEvent<unknown>, value: "current" | "proposals") => {
    reload();
    switchTab(uuid)(value);
  };

  useEffect(() => {
    if (!uuid) {
      navigate(getPriceListHomeUrl());
      return;
    }

    if (!["current", "proposals"].includes(params.tab ?? "")) {
      navigate(getPriceListDetailsUrl(uuid));
    }
  }, [navigate, params.tab, uuid]);

  return (
    <Container maxWidth="md" disableGutters className={classes.container}>
      <BreadcrumbMarkers
        breadcrumbs={[
          { title: "Cenniki", id: "priceLists", to: getPriceListHomeUrl() },
          { title: `Cennik ${priceListDetail.name}`, id: "priceList" },
        ]}
      />
      <PriceListName name={priceListDetail.name} renamePriceList={renamePriceList} />
      {permissions.viewProposals && (
        <Tabs
          className={classes.tabs}
          value={params.tab ?? "current"}
          onChange={handleChange}
          centered
          indicatorColor="primary"
        >
          <Tab label="Aktualny cennik" value="current" />
          <Tab label="Propozycje zmian" value="proposals" />
        </Tabs>
      )}
      {params.tab === "current" && (
        <PriceListCurrentContainer uuid={uuid} priceListDetail={priceListDetail} tradeItems={tradeItems} />
      )}
      {params.tab === "proposals" && permissions.viewProposals && (
        <ProposalsContainer uuid={uuid} currency={priceListDetail.currency} tradeItems={tradeItems} />
      )}
    </Container>
  );
};

const useStyles = makeStyles((theme) => {
  return {
    container: {},
    title: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(2),
    },
    tabs: {
      marginBottom: theme.spacing(4),
    },
  };
});
